// @flow

import * as R from "ramda";
import { BigImage, Image, Tag } from "../common";
import { pathOr } from "ramda";
import { Col, Row, Divider } from "antd";
import { useDispatch, useEffect, useGlobal } from "reactn";
import React, { type ChildrenArray } from "reactn";
import Skeleton from "react-loading-skeleton";
import moment from "moment-timezone";
import styled, { type StyledComponent } from "styled-components";
import { GeoButton } from "views/components/GeoButton";
import { FavoriteButton } from "views/components/FavoriteButton";
import { Spacer } from "views/components/common/Spacer";
import { LogoContainer } from "views/mobile/components/LogoContainer";
import { AttentionTypeLabel } from "views/components/Office/AttentionTypeLabel";
import { IconText } from "views/components/IconText";
import theme, {
	type Theme,
	getColor,
	getSize,
	getWeight,
} from "views/resources/theme";
import type { Office } from "types";

interface Props {
	office: ?Office;
	transparent?: boolean;
}

const OfficeHours = ({ office }) => {
	const getHourBlock = useDispatch("getHourBlock");
	useEffect(() => {
		if (!office) return;
		getHourBlock(office);
	}, []);
	if (!office || !office.hours)
		return (
			<Tag type="disabled">
				<Skeleton with={7} />
			</Tag>
		);
	if (office && office.hours && office.hours.length < 1)
		return (
			<Tag type="disabled">
				<Skeleton width={7} />
			</Tag>
		);
	const now = moment().tz(office.timezone || "America/Santiago");
	const toMoment = (time) =>
		moment.tz(now.format("YYYY-MM-DD") + "T" + time, office.timezone);
	const isOpen =
		office.hours &&
		office.hours
			.filter((h) => h.weekday == now.weekday())
			.map((h) => ({
				from: toMoment(h.from),
				to: toMoment(h.to),
			}))
			.some((h) => now.isBetween(h.from, h.to));
	return isOpen ? (
		<Tag type="success" className="tag">
			Abierto
		</Tag>
	) : (
		<Tag type="disabled" className="tag">
			Cerrado
		</Tag>
	);
};

const OfficeDetailHeader = ({ desktop, office }) => {
	const Component = desktop ? BigImage : Image;
	const logo = pathOr(false, ["options", "brand", "logo"], office);
	const showLogo = pathOr(false, ["options", "brand", "show_logo"], office);

	return (
		<CardHeader desktop={desktop} className="card-header">
			<Component>
				{office ? (
					<LogoContainer
						logo={logo}
						showLogo={showLogo}
						office={office}
					/>
				) : (
					<Skeleton />
				)}
			</Component>
			<h5>{office ? office.name : <Skeleton />}</h5>
		</CardHeader>
	);
};

// TODO: name logos with the category id instead of the slug
const OfficeDetailCard = ({
	office,
	transparent,
	createFavorite,
	desktop,
	user,
	disabledOffice,
}) => {
	const distance = R.pathOr(false, ["distance"], office);
	const kmDistance =
		office &&
		distance &&
		!isNaN(distance.distance) &&
		distance.distance / 1000;
	const distanceExists = distance && distance.distance !== Infinity;
	const officeId = R.pathOr(false, ["id"], office);
	const string_distance = distanceExists
		? Math.floor(distance.distance) < 1000
			? `${distance.distance.toFixed(0)} ${distance.unit}`
			: `${kmDistance.toFixed(1)} ${distance.unit}`
		: "";

	const offLatitude = R.pathOr(false, ["lat"], office);
	const offLongitude = R.pathOr(false, ["lng"], office);
	const userLat = R.pathOr(false, ["position", "lat"], user);
	const userLng = R.pathOr(false, ["position", "lon"], user);
	const isOfficeOpen = R.pathOr(false, ["online"], office);
	const attentionType = pathOr(
		"face2face",
		["options", "reservations", "attention_type"],
		office
	);

	const showGeoButton =
		offLatitude && offLongitude && userLat && userLng ? true : false;
	const [config] = useGlobal("UIConfig");

	const { hideOfficeLabel, hideFavoritesButton } = config;

	return (
		<Wrapper bg={transparent ? "transparent" : officeColor(office)}>
			<Card>
				<OfficeDetailHeader desktop={desktop} office={office} />
				{!hideOfficeLabel && (
					<Row gutter={18} style={{ position: "relative" }}>
						<Col
							style={{
								position: "absolute",
								left: 105,
								bottom: 38,
							}}
						>
							<AttentionTypeLabel attentionType={attentionType} />
						</Col>
					</Row>
				)}

				{string_distance && !hideOfficeLabel && (
					<Row gutter={18} style={{ position: "relative" }}>
						<DistanceCol>
							<IconText
								value={string_distance}
								iconType="location"
							></IconText>
						</DistanceCol>
					</Row>
				)}
			</Card>

			{disabledOffice && (
				<h4
					style={{
						textAlign: "center",
						width: "100%",
						background: "#f0f0f0",
					}}
				>
					Sucursal no disponible
				</h4>
			)}
			<Divider style={{ marginBottom: 0, marginTop: 8 }} />

			{!hideFavoritesButton && (
				<CardRow key="4" className="buttons__container-geolocation">
					<GeoButton
						userLng={userLng}
						userLat={userLat}
						offLatitude={offLatitude}
						offLongitude={offLongitude}
						desktop
						borderless
						disabled={showGeoButton}
					/>

					<FavoriteButton borderless officeId={officeId} />
				</CardRow>
			)}
		</Wrapper>
	);
};

const StatusLabel = ({ color, label }) => {
	const getColor = color && color === "green" ? "#7cb305" : "#ec1c24";
	return <div style={{ color: getColor }}>{label}</div>;
};

const officeColor = (office) =>
	// $FlowFixMe
	R.pathOr(theme.colors.bg, ["options", "color"], office);
// $FlowFixMe
const hoursMessage = (office) =>
	// $FlowFixMe
	R.pathOr(null, ["options", "hours_message"], office);

OfficeDetailCard.defaultProps = {
	transparent: false,
};

type WrapperProps = {|
	bg: string,
	children: ChildrenArray<any>,
|};

const Wrapper: StyledComponent<WrapperProps, Theme, *> = styled.div`
	background-color: ${(props) => props.bg || getColor("primary")(props)};
	.buttons__container-geolocation {
		background: #ebf2fe;
		height: 52px;
	}
`;

const Card: StyledComponent<{}, Theme, *> = styled.div`
	padding-left: 2rem;
	background-color: #fff;
	margin-bottom: 2rem;
	height: 99px;

	.card-header img {
		margin-top: 1.5rem;
	}

	h5 {
		font-size: 14px;
		font-weight: 600;
		line-height: 22px;
		margin: 0 1.5rem 0 1.5rem;
	}
`;
const CardHeader = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: ${getSize("m")};
	flex-direction: ${(props) => (props.desktop ? "column" : "row")};
	& h5 {
		margin-top: ${(props) => (props.desktop ? "20px" : "inherit")};
		margin-bottom: 12px;
		font-size: 16px;
	}
`;

const CardRow = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: ${getSize("s")};
	p {
		font-size: ${getSize("sm")};
		margin: 0;
	}
	.tag {
		margin-left: auto;
	}
	&:last-child {
		margin: 0;
	}
`;

const DistanceCol = styled(Col)`
	position: absolute;
	left: 105px;
	bottom: 4px;
	p {
		font-size: 12px;
	}
`;

export default OfficeDetailCard;
